// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from '@emotion/styled';
import { compose } from 'recompose';
import { sumBy, sum, get, flow, pickBy, keys } from 'lodash/fp';
import Helmet from 'react-helmet';
import pluralize from 'pluralize';
import Page from 'app/containers/parts/Page';
import t from 'app/utils/translate';
import UserOnly from 'app/components/util/UserOnly';
import Grid from 'app/components/layout/Grid';
import GridCol from 'app/components/layout/GridCol';
import Responsive from 'app/components/responsive/Responsive';
import GroupedChallengesList from 'app/components/challenges/GroupedChallengesList';
import compatibleChallengesSelector from 'app/selectors/compatibleChallengesSelector';
import * as compatibleChallengesActions from 'app/actions/compatibleChallengesActions';
import LineUpTable from 'app/components/challenge/LineUpTable';
import LineUpHeader from 'app/components/challenge/LineUpHeader';
import LineUpFooter from 'app/components/challenge/LineUpFooter';
import { getCostSplit } from 'app/utils/priceUtils';
import withPxc from '../hoc/withPxc';
import LineUpSection from '../components/challenge/LineUpSection';
import OpenChallengeModal from '../components/challenge/OpenChallengeModal';
import SelectedEntityRow from '../components/challenge/SelectedEntityRow';
import Button from '../components/commons/Button';
import Checkbox from '../components/commons/Checkbox';
import Icon from '../components/commons/Icon';
import Loader from '../components/commons/Loader';
import Modal from '../components/commons/Modal';
import Title from '../components/commons/Title';
import FormValidationMessage from '../components/commons/FormValidationMessage';
import selectionSelector from '../selectors/selectionSelector';
import FixToViewport from '../components/commons/FixToViewport';
import { getFactoredEntityPositionSalary } from '../selectors/salaryExpenditureSelector';

pluralize.addPluralRule(/entry$/i, 'entries');

type Props = {
  // balance?: number,
  bonusBalance?: number,
  bonusFreeTotalCost: number,
  slateName?: string,
  challenges: Array<Object>,
  challengesData: Array<Object>,
  currencyCode: string,
  decrementPendingEntry: (id: string) => void,
  entryCount?: number,
  errorMessages: Array<string>,
  startTime?: number | string,
  getCompatibleChallenges: () => void,
  incrementPendingEntry: (id: string) => void,
  isEnteringChallenges: boolean,
  isLoading: boolean,
  isRacing?: boolean,
  numFailed: number,
  numPendingEntries: number,
  remainingSalary?: number,
  pendingEntries: Object,
  positions: Array<Object>,
  pxc?: Object,
  resetCompatibleChallenges: () => void,
  showCustom: boolean,
  showH2H: boolean,
  sportIcon?: string,
  submissionComplete: boolean,
  submitMultipleEntries: () => void,
  successfulEntryIds: [],
  toggleShowCustom: () => void,
  toggleShowH2H: () => void,
  totalCost: number
};

type State = {
  addedToCart?: boolean,
  willSubmit?: boolean
};

const LineUpWrapper = styled.div({
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column'
});

const TableWrapper = styled.div({
  flex: '0 1 auto',
  overflowX: 'hidden'
});

const SubmitButton = styled(Button)({
  minHeight: 51
});

const SmallButton = styled(Button)(
  {
    minHeight: 51,
    maxWidth: 200
  },
  ({ theme }) => ({
    color: theme.color.basis4,
    marginTop: theme.spacing.small
  })
);

const ButtonsWrapper = styled.div({
  display: 'flex',
  justifyContent: 'center'
});

const ModalButton = styled(Button)({
  minWidth: '6rem',
  margin: '0.5em',
  textAlign: 'center',
  minHeight: 51
});

const SubmitButtonWrapper = styled.div({
  alignItems: 'stretch'
});

const FinePrint = styled.div(({ theme }) => ({
  fontSize: theme.fontSize.tiny,
  color: theme.color.basis4
}));

class EnterAdditionalChallenges extends React.Component<Props, State> {
  props: Props;

  state: State = {
    addedToCart: false,
    willSubmit: false
  };

  static defaultProps = {
    // balance: undefined,
    bonusBalance: undefined,
    entryCount: undefined,
    isRacing: false,
    remainingSalary: undefined,
    sportIcon: undefined,
    startTime: undefined,
    slateName: undefined,
    pxc: undefined
  };

  componentDidMount() {
    const { getCompatibleChallenges } = this.props;

    getCompatibleChallenges();
  }

  componentWillUnmount() {
    const { resetCompatibleChallenges } = this.props;
    resetCompatibleChallenges();
  }

  previewTransfer = ({ bonus = 0 }) => {
    const { challengesData, pendingEntries, pxc } = this.props;
    const transactions = [];

    let remainBonus = bonus;

    Object.keys(pendingEntries).forEach(challengeId => {
      const ch = challengesData[0].challenges.find(c => c.id === challengeId);

      if (ch && pendingEntries[challengeId] > 0) {
        for (let i = 0; i < pendingEntries[challengeId]; i += 1) {
          if (remainBonus >= ch.cost) {
            remainBonus -= ch.cost;
            continue; // eslint-disable-line
          }

          let thisCost = ch.cost;

          if (remainBonus > 0) {
            thisCost -= remainBonus;
            remainBonus = 0;
          }

          transactions.push({
            amount: thisCost,
            details: `${ch.name} (Entry Fee)`,
            metadata: {
              challenge_id: challengeId,
              multi: true
            }
          });
        }
      }
    });

    if (transactions.length) {
      const transferData = {
        transactions
      };

      pxc.previewTransfer(transferData);
    }
  };

  handleCounterClick = (challengeId, direction) => {
    const { decrementPendingEntry, incrementPendingEntry } = this.props;
    if (direction === -1) {
      decrementPendingEntry(challengeId);
    } else {
      incrementPendingEntry(challengeId);
    }
  };

  getProducts = () => {
    const { challenges, pendingEntries } = this.props;
    const products = [];

    const filteredPendingEntries = flow(
      pickBy(count => count),
      keys
    )(pendingEntries);

    if (!filteredPendingEntries) {
      return products;
    }

    filteredPendingEntries.forEach(challengeId => {
      const challenge = challenges.find(ch => ch.id === challengeId);
      if (challenge) {
        const { id, name, isRapidfire, isH2H, cost, sportName } = challenge;
        let brand = cost === 0 ? 'Free' : 'Paid';

        if (isRapidfire) {
          brand = 'Rapidfire';
        } else if (isH2H) {
          brand = 'Head to head';
        }

        products.push({
          name,
          id,
          brand,
          category: sportName,
          price: cost,
          quantity: pendingEntries[challengeId]
        });
      }
    });

    return products;
  };

  handleCancel = () => {
    const { currencyCode } = this.props;
    const { addedToCart } = this.state;

    if (addedToCart && window && window.dataLayer) {
      const products = this.getProducts();

      if (products) {
        window.dataLayer.push({
          event: 'removeFromCart',
          ecommerce: {
            currencyCode,
            remove: {
              products
            }
          }
        });
      }
    }

    this.setState({
      addedToCart: false,
      willSubmit: false
    });
  };

  handleSubmit = () => {
    const { currencyCode } = this.props;

    if (window && window.dataLayer) {
      const products = this.getProducts();

      if (products) {
        window.dataLayer.push({
          event: 'addToCart',
          ecommerce: {
            currencyCode,
            add: {
              products
            }
          }
        });

        window.dataLayer.push({
          event: 'checkout',
          ecommerce: {
            currencyCode,
            checkout: {
              actionField: {
                step: 1
              },
              products
            }
          }
        });
      }
    }

    this.setState({
      addedToCart: true,
      willSubmit: true
    });
  };

  handleConfirm = () => {
    const {
      // balance,
      bonusBalance,
      pxc,
      currencyCode,
      submitMultipleEntries,
      totalCost
    } = this.props;

    const balance =
      (window && window.portal && window.portal.user && window.portal.user.account_balance()) || 0;

    if (window && window.dataLayer) {
      const products = this.getProducts();

      if (products) {
        window.dataLayer.push({
          event: 'checkout',
          ecommerce: {
            currencyCode,
            checkout: {
              actionField: {
                step: 2
              },
              products
            }
          }
        });
      }

      this.setState({ addedToCart: false });
    }

    const { bonus } = getCostSplit(totalCost, balance, bonusBalance);

    if (!pxc.isEnabled || totalCost - bonus <= 0) {
      submitMultipleEntries();
      return;
    }

    this.previewTransfer({ bonus });
  };

  renderSubmitButton = ({ autoBorderRadius = true } = {}) => {
    const { numPendingEntries } = this.props;
    return (
      <SubmitButton
        disabled={numPendingEntries === 0}
        size="medium"
        kind="primary"
        onClick={this.handleSubmit}
        block
        autoBorderRadius={autoBorderRadius}
      >
        <span>
          Submit {numPendingEntries} {pluralize('entry', numPendingEntries)}
        </span>
      </SubmitButton>
    );
  };

  render() {
    const {
      bonusFreeTotalCost,
      slateName,
      challengesData,
      entryCount,
      errorMessages,
      isEnteringChallenges,
      isLoading,
      isRacing,
      numPendingEntries,
      numFailed,
      remainingSalary,
      successfulEntryIds,
      positions,
      pxc,
      showCustom,
      showH2H,
      sportIcon,
      startTime,
      submissionComplete,
      totalCost,
      toggleShowCustom,
      toggleShowH2H
    } = this.props;

    const { willSubmit } = this.state;

    return (
      <UserOnly>
        <Page title={t('Enter Additional Challenges')}>
          <Helmet title={t('Enter Additional Challenges')} />
          <Responsive desktopLarge>
            {isDesktop => (
              <Grid>
                {isDesktop && (
                  <GridCol width="one-third">
                    {isLoading ? (
                      <Loader />
                    ) : (
                      <LineUpWrapper>
                        <LineUpHeader
                          name={slateName}
                          numEntries={entryCount}
                          sportIcon={sportIcon}
                          startTime={startTime}
                          remainingSalary={remainingSalary}
                        />
                        <TableWrapper>
                          <LineUpTable
                            positions={positions}
                            renderPosition={(pos, i) => (
                              <LineUpSection
                                key={`${pos.id}_${i}`}
                                abbr={pos.abbr}
                                filled={pos.filled}
                                hasError={pos.hasError}
                                id={pos.id}
                                name={pos.name}
                                selections={pos.entities}
                                renderEntity={entity => (
                                  <SelectedEntityRow
                                    key={entity.id}
                                    awayTeam={entity.awayAbbr}
                                    bonus={entity.bonus}
                                    disabled={entity.disabled}
                                    gameOrder={entity.gameOrder}
                                    showButtons={false}
                                    homeTeam={entity.homeAbbr}
                                    id={entity.id}
                                    idStr={entity.idStr}
                                    image={entity.imageURI}
                                    initials={entity.initials}
                                    isHome={entity.isHome}
                                    isRacing={isRacing}
                                    isScratched={entity.isScratched}
                                    labels={entity.labels}
                                    locked={entity.locked}
                                    name={entity.name}
                                    place={entity.placeProjected}
                                    positionId={pos.id}
                                    positionLabel={pos.abbr}
                                    reverseOrder={entity.reverseOrder}
                                    rider={entity.rider}
                                    riderImage={entity.riderImage}
                                    salary={getFactoredEntityPositionSalary(
                                      entity.salary,
                                      entity.positions,
                                      pos.key
                                    )}
                                    trainer={entity.trainer}
                                    win={entity.winProjected}
                                  />
                                )}
                              />
                            )}
                          />
                        </TableWrapper>
                        <LineUpFooter totalCost={totalCost} numSelected={numPendingEntries} />
                        {this.renderSubmitButton()}
                      </LineUpWrapper>
                    )}
                  </GridCol>
                )}
                <GridCol width={isDesktop ? 'two-thirds' : undefined}>
                  <GroupedChallengesList
                    challengesData={challengesData}
                    isFetching={isLoading}
                    inline
                    onCounterClick={this.handleCounterClick}
                    disableRowClick
                    isTwoThirdSize={true}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '5px' }}>
                    <Checkbox checked={showCustom} onChange={toggleShowCustom} lightContent>
                      Show Custom {t('Challenges')}
                    </Checkbox>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '5px' }}>
                    <Checkbox checked={showH2H} onChange={toggleShowH2H} lightContent>
                      Show H2H
                    </Checkbox>
                  </div>
                  <SmallButton size="medium" kind="bare" link="/my-teams" block>
                    <Icon type="icon_menu_my_teams" /> My Teams
                  </SmallButton>
                  {!isDesktop && (
                    <Responsive mobile>
                      {isMobile => (
                        <FixToViewport disabled={!isMobile} placement="bottom" fillSpace>
                          <LineUpFooter totalCost={totalCost} numSelected={numPendingEntries} />
                          <SubmitButtonWrapper>
                            {this.renderSubmitButton({
                              autoBorderRadius: false
                            })}
                          </SubmitButtonWrapper>
                        </FixToViewport>
                      )}
                    </Responsive>
                  )}
                </GridCol>
              </Grid>
            )}
          </Responsive>
        </Page>
        {willSubmit && !submissionComplete && (
          <OpenChallengeModal
            cost={totalCost}
            bonusFreeCost={bonusFreeTotalCost}
            showSubmitSuccess={submissionComplete}
            isSubmitting={isEnteringChallenges}
            onCancel={this.handleCancel}
            multiEntry={numPendingEntries > 1}
            onSubmit={this.handleConfirm}
            pxc={pxc}
            showEntryConfirmation
          />
        )}
        {submissionComplete && (
          <Modal isOpen size="medium" showCloseButton={false}>
            <Title noWhiteSpace>{successfulEntryIds.length > 0 ? 'Success' : 'Fail'}</Title>
            {successfulEntryIds.length > 0 && (
              <div style={{ textAlign: 'center', padding: '1em' }}>
                You have successfully submitted {successfulEntryIds.length}{' '}
                {pluralize('entry', successfulEntryIds.length)}!
                <FinePrint>
                  Your tickets: {successfulEntryIds.map(id => `#${id}`).join(', ')}
                </FinePrint>
              </div>
            )}
            {numFailed > 0 && (
              <FormValidationMessage
                message={[
                  {
                    message: `Unfortunately ${numFailed} ${pluralize('entry', numFailed)} failed.`
                  },
                  ...errorMessages.map(str => ({
                    message: str
                  }))
                ]}
              />
            )}
            <ButtonsWrapper>
              <ModalButton kind="secondary" size="medium" link="/lobby">
                Lobby
              </ModalButton>
              <ModalButton kind="primary" size="medium" link="/my-challenges">
                {t('My Challenges')}
              </ModalButton>
              <ModalButton kind="primary" size="medium" link="/my-teams">
                {t('My Teams')}
              </ModalButton>
            </ButtonsWrapper>
          </Modal>
        )}
      </UserOnly>
    );
  }
}

function mapStateToProps(state: GlobalState) {
  const {
    challenges,
    errorMessages,
    isEnteringChallenges,
    isLoading,
    isRacing,
    pendingEntries,
    lineup,
    showCustom,
    showH2H,
    submissionComplete,
    successfulEntryIds
  } = state.compatibleChallenges;
  const challengesData = compatibleChallengesSelector(state);
  const totalCost = sumBy(ch => pendingEntries[ch.id] * ch.cost, challenges);
  const bonusFreeTotalCost = sumBy(
    ch => (ch.isH2H ? pendingEntries[ch.id] * ch.cost : 0),
    challenges
  );
  const numPendingEntries = sum(Object.values(pendingEntries));

  return {
    // balance: get('user.currentUser.balance', state),
    bonusBalance: get('user.currentUser.bonusPack.totalBalance', state),
    bonusFreeTotalCost,
    slateName: get('slate.name', lineup),
    challenges,
    challengesData,
    currencyCode: get('settings.currency.code', state),
    entryCount: get('entryCount', lineup),
    errorMessages,
    isEnteringChallenges,
    isLoading,
    isRacing,
    numFailed: numPendingEntries - successfulEntryIds.length,
    numPendingEntries,
    pendingEntries,
    positions: selectionSelector(state),
    remainingSalary: get('remainingSalary', lineup),
    showCustom,
    showH2H,
    sportIcon: get('sportIcon', lineup),
    startTime: get('slate.start_time', lineup),
    submissionComplete,
    successfulEntryIds,
    totalCost
  };
}

function mapDispatchToProps(dispatch, { match, isPxcEnabled }) {
  const { lineupId } = match.params;

  return {
    getCompatibleChallenges() {
      dispatch(compatibleChallengesActions.getCompatibleChallenges({ lineup_id: lineupId }));
    },
    submitMultipleEntries() {
      if (isPxcEnabled) {
        dispatch(compatibleChallengesActions.submitMultiplePxcEntries());
      } else {
        dispatch(compatibleChallengesActions.submitMultipleEntries());
      }
    },
    decrementPendingEntry(id) {
      dispatch(compatibleChallengesActions.decrementPendingEntry(id));
    },
    incrementPendingEntry(id) {
      dispatch(compatibleChallengesActions.incrementPendingEntry(id));
    },
    resetCompatibleChallenges() {
      dispatch(compatibleChallengesActions.resetCompatibleChallenges());
    },
    toggleShowCustom() {
      dispatch(compatibleChallengesActions.toggleShowCustom());
    },
    toggleShowH2H() {
      dispatch(compatibleChallengesActions.toggleShowH2H());
    }
  };
}

const enhancer = compose(withRouter, withPxc, connect(mapStateToProps, mapDispatchToProps));

export default enhancer(EnterAdditionalChallenges);
